import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../ui/Modal';
import InfoPopover from '../ui/InfoPopover';
import type { ContentMasterWawiLanguageView } from '../../api/generated/models/ContentMasterWawiLanguageView';
import type { ContentMasterWorkspaceDefaultLanguageView } from '../../api/generated/models/ContentMasterWorkspaceDefaultLanguageView';
import type { ConfigView } from '../../api/generated/models/ConfigView';
import {
  isWawiLanguageCodeAllowed,
  pickDefaultWawiLanguageCode,
  wawiLanguageLabel,
  wawiLanguageToApiValue,
} from '../../utils/contentMasterWawiLanguage';
import { estimateContentMasterAiTokens } from '../../utils/contentMasterTokenEstimate';

/** Payload handed back to the page when the user confirms the modal. */
export interface BulkTranslateConfirmParams {
  /** Language to translate FROM (one active shop language). */
  sourceLanguage: string;
  /** Languages to translate INTO (one or more, never equal to sourceLanguage). */
  targetLanguages: string[];
  /**
   * Opt-in for the URL path (`urlPath` / `cUrlPfad`) - same semantics and the
   * same warning as on bulk generate: live shop slugs are only replaced with
   * explicit consent. Default false.
   */
  includeUrlPath: boolean;
}

export interface BulkTranslateConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCount: number;
  languages: ContentMasterWawiLanguageView[];
  defaultWorkspaceLanguage?: ContentMasterWorkspaceDefaultLanguageView | null;
  /** Current list language - preselected as the source language when valid. */
  initialLanguage: string;
  preferDeUi: boolean;
  isSubmitting: boolean;
  /**
   * Public global configs (token cost model) for the translate-tariff
   * estimate; the estimate row is hidden when the required keys are missing.
   */
  publicConfigs?: ConfigView[] | null;
  onConfirm: (params: BulkTranslateConfirmParams) => void;
}

/**
 * Confirmation modal for the bulk TRANSLATE flow (`POST /bulk/translate`).
 * The user picks ONE source language and ONE OR MORE target languages
 * (a target equal to the source is disabled client-side - the backend would
 * reject it with 400 anyway). There is deliberately no field picker: the
 * server resolves what is translatable (core fields + Wawi varchar attributes
 * + SCX marketplace text attributes).
 */
const BulkTranslateConfirmModal: React.FC<BulkTranslateConfirmModalProps> = ({
  isOpen,
  onClose,
  selectedCount,
  languages,
  defaultWorkspaceLanguage,
  initialLanguage,
  preferDeUi,
  isSubmitting,
  publicConfigs,
  onConfirm,
}) => {
  const { t } = useTranslation();

  const [sourceLanguage, setSourceLanguage] = useState<string>(() => {
    if (initialLanguage && isWawiLanguageCodeAllowed(initialLanguage, languages)) {
      return initialLanguage;
    }
    return pickDefaultWawiLanguageCode(languages, defaultWorkspaceLanguage);
  });
  const [targetLanguages, setTargetLanguages] = useState<string[]>([]);
  const [includeUrlPath, setIncludeUrlPath] = useState(false);

  // Re-seed local state every time the modal is reopened so a closed-then-
  // reopened modal always reflects the current page filters, not a stale
  // selection from a previous open (same pattern as BulkGenerateConfirmModal).
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (initialLanguage && isWawiLanguageCodeAllowed(initialLanguage, languages)) {
      setSourceLanguage(initialLanguage);
    } else {
      setSourceLanguage(pickDefaultWawiLanguageCode(languages, defaultWorkspaceLanguage));
    }
    setTargetLanguages([]);
    setIncludeUrlPath(false);
  }, [isOpen, initialLanguage, languages, defaultWorkspaceLanguage]);

  /** Unique language options: `{ code, label }` from the active Wawi languages. */
  const languageOptions = useMemo(
    () =>
      languages
        .map((lang, idx) => {
          const code = wawiLanguageToApiValue(lang);
          if (!code) {
            return null;
          }
          return {
            key: `${code}-${lang.kSprache ?? idx}`,
            code,
            label: wawiLanguageLabel(lang, preferDeUi),
          };
        })
        .filter((v): v is { key: string; code: string; label: string } => v != null),
    [languages, preferDeUi]
  );

  const toggleTarget = (code: string) => {
    setTargetLanguages((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  // A target equal to the source is never sent (backend 400): drop it from the
  // selection whenever the source changes.
  const effectiveTargets = useMemo(
    () => targetLanguages.filter((code) => code !== sourceLanguage),
    [targetLanguages, sourceLanguage]
  );

  /**
   * Translate-tariff token estimate: base rate per (product × target
   * language) with `fieldsCount = 0` - the worker resolves the field list
   * server-side, so this is the base-rate lower bound (same convention as the
   * bulk generate estimate).
   */
  const tokenEstimate = useMemo<number | null>(
    () =>
      estimateContentMasterAiTokens(
        publicConfigs,
        0,
        selectedCount * effectiveTargets.length,
        'translate'
      ),
    [publicConfigs, selectedCount, effectiveTargets.length]
  );

  const canSubmit =
    !isSubmitting &&
    selectedCount > 0 &&
    Boolean(sourceLanguage) &&
    effectiveTargets.length > 0;

  const handleConfirm = () => {
    if (!canSubmit) {
      return;
    }
    onConfirm({
      sourceLanguage,
      targetLanguages: effectiveTargets,
      includeUrlPath,
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isSubmitting ? () => undefined : onClose}
      title=""
      hideCloseButton
      isCentered
      className="content-master-bulk-modal"
    >
      <div className="content-master-bulk-modal__inner">
        <h3 className="content-master-bulk-modal__title">
          {t('content_master_bulk_translate_modal_title')}
          <InfoPopover
            text={t('content_master_bulk_translate_scope_hint')}
            ariaLabel={t('content_master_bulk_translate_modal_title')}
          />
        </h3>
        <div className="content-master-bulk-modal__subtitle">
          {t('content_master_bulk_modal_subtitle', { count: selectedCount })}
        </div>

        <label className="content-master-bulk-modal__field">
          <span className="content-master-bulk-modal__field-label">
            {t('content_master_bulk_translate_source_language')}
          </span>
          <select
            className="content-master-bulk-modal__select"
            value={sourceLanguage}
            disabled={isSubmitting || languageOptions.length === 0}
            onChange={(event) => setSourceLanguage(event.target.value)}
          >
            {languageOptions.map((opt) => (
              <option key={opt.key} value={opt.code}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>

        <fieldset className="content-master-bulk-modal__groups">
          <legend className="content-master-bulk-modal__field-label">
            {t('content_master_bulk_translate_target_languages')}
          </legend>
          {languageOptions.map((opt) => {
            const isSource = opt.code === sourceLanguage;
            return (
              <label key={opt.key} className="content-master-bulk-modal__checkbox">
                <input
                  type="checkbox"
                  className="content-master-checkbox"
                  checked={!isSource && targetLanguages.includes(opt.code)}
                  disabled={isSubmitting || isSource}
                  onChange={() => toggleTarget(opt.code)}
                />
                <span>{opt.label}</span>
              </label>
            );
          })}
        </fieldset>

        <label className="content-master-bulk-modal__checkbox">
          <input
            type="checkbox"
            className="content-master-checkbox"
            checked={includeUrlPath}
            disabled={isSubmitting}
            onChange={(event) => setIncludeUrlPath(event.target.checked)}
          />
          <span>{t('content_master_bulk_translate_include_url_path')}</span>
          <InfoPopover
            text={t('content_master_bulk_include_url_path_hint')}
            ariaLabel={t('content_master_bulk_translate_include_url_path')}
          />
        </label>

        {tokenEstimate != null && tokenEstimate > 0 && (
          <div className="content-master-bulk-modal__token-estimate">
            <span className="content-master-bulk-modal__token-estimate-label">
              {t('content_master_bulk_modal_token_estimate_label')}
            </span>
            <span className="content-master-bulk-modal__token-estimate-value">
              {Math.round(tokenEstimate).toLocaleString()}{' '}
              {t('content_master_bulk_modal_token_estimate_unit')}
            </span>
          </div>
        )}

        <div className="content-master-bulk-modal__buttons">
          <button
            type="button"
            className="content-master-bulk-modal__button content-master-bulk-modal__button--cancel"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('content_master_bulk_modal_cancel')}
          </button>
          <button
            type="button"
            className="content-master-bulk-modal__button content-master-bulk-modal__button--confirm"
            onClick={handleConfirm}
            disabled={!canSubmit}
          >
            {t('content_master_bulk_translate_modal_start')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default BulkTranslateConfirmModal;
